import { Injectable, computed, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ApiError } from '../api-error';
import { HttpService } from '../http.service';
import { ConnectivityService } from '../pwa/connectivity.service';
import { WRITES, clearOfflineStores, transact as run } from './offline-db';

/** One write made without a connection, kept until the API accepts it. */
export interface QueuedWrite {
  /** Client-generated: the same entry, wherever it is read from. */
  id: string;
  method: 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  /** The API path after `environment.apiUrl`, exactly as the screen would have called it. */
  url: string;
  body: unknown;
  /** Sent as `Idempotency-Key`, so an endpoint that honours it (the public API, mobile stock) answers a retry with the first answer. */
  idempotencyKey: string;
  createdAt: number;
  /** i18n key of the change ("Approve the leave request"): the pending list names it in the user's language. */
  labelKey: string;
  /** The login that made the change. A shared browser never sends another login's queued work. */
  userId: string;
  companyId: string;
  /** How many times the API answered "still running" (409) for this entry; old entries have none. */
  conflicts?: number;
}

/** A change the API keeps answering 409 to is a conflict (someone else changed or decided it first), not a wait. */
export const MAX_CONFLICTS = 3;
/** A change older than this is dropped as expired: the person who made it no longer remembers, and the data has moved on. */
export const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

/** What a replay ended with, for the one entry it tried. */
type Outcome = 'sent' | 'retry' | 'conflict' | 'refused';

/** A refusal the user has to hear about (the API will never accept this change): the label and why, as i18n keys. */
export interface RefusedWrite {
  labelKey: string;
  reasonKey: string;
}

const newId = (): string =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`;

/** Empties the queue (and the saved copies) on this device, every login: the specs, since IndexedDB outlives a run. */
export const clearOfflineQueue = clearOfflineStores;

/**
 * OPS-03-T01: writes made without a connection are kept on this device and sent, oldest first, when it returns. The queue is
 * scoped to the login that made the change — a browser that signs in as someone else neither shows nor sends it.
 *
 * A retry is never sent twice at once (`running`), an accepted write leaves the queue for good, and a refusal the API will
 * never accept (a validation answer) is dropped and reported instead of blocking everything behind it. A 409 is retried a few
 * times (the first try may still be running) and then reported as a conflict; a change a week old is dropped as expired.
 * Endpoints that define `Idempotency-Key` (the public API, mobile stock) answer a retry with the first answer; the rest rely
 * on the queue's one-at-a-time replay, because a request that never reached the server is safe to repeat and one that did is
 * only repeated when its status is unknown — which only the API can finally settle.
 */
@Injectable({ providedIn: 'root' })
export class OfflineQueueService {
  private readonly http = inject(HttpService);
  private readonly connectivity = inject(ConnectivityService);

  private readonly items = signal<QueuedWrite[]>([]);
  /** The queued writes of the signed-in login, oldest first. */
  readonly pending = computed(() => this.items().filter((item) => this.mine(item)));
  readonly count = computed(() => this.pending().length);
  /** The last change the API refused, until the user sees it. */
  readonly refused = signal<RefusedWrite | null>(null);

  private running = false;
  private started = false;
  /** Two changes in the same millisecond still go out in the order they were made. */
  private lastStamp = 0;

  /** Starts the queue: read what this login left behind and send it as soon as there is a connection. */
  start(): void {
    if (this.started) return;
    this.started = true;
    void this.refresh();
    window.addEventListener('online', () => void this.replay());
    void this.replay();
  }

  /** Keeps a write that could not be sent. The caller already knows it is safe to repeat (it never reached the server). */
  async enqueue(write: Pick<QueuedWrite, 'method' | 'url' | 'body' | 'labelKey'>): Promise<QueuedWrite> {
    const entry: QueuedWrite = {
      ...write,
      id: newId(),
      idempotencyKey: newId(),
      createdAt: Math.max(Date.now(), this.lastStamp + 1),
      userId: sessionStorage.getItem('id') ?? '',
      companyId: sessionStorage.getItem('companyId') ?? '',
    };
    this.lastStamp = entry.createdAt;
    await run(WRITES, 'readwrite', (store) => store.put(entry));
    this.items.update((items) => [...items.filter((item) => item.id !== entry.id), entry].sort((a, b) => a.createdAt - b.createdAt));
    return entry;
  }

  /** Sends what is waiting, oldest first, and stops at the first answer that is not a success or a final refusal. */
  async replay(): Promise<void> {
    if (this.running || !this.connectivity.online()) return;
    this.running = true;
    try {
      await this.refresh();
      for (const entry of this.pending()) {
        // Too old to be worth sending: say so and move on to the next one.
        if (Date.now() - entry.createdAt > MAX_AGE_MS) {
          await this.drop(entry, 'pwa.queue.expired');
          continue;
        }
        const outcome = await this.send(entry);
        if (outcome === 'sent') {
          await this.drop(entry, null);
          continue;
        }
        if (outcome === 'refused') await this.drop(entry, 'pwa.queue.refused');
        else if (outcome === 'conflict') await this.drop(entry, 'pwa.queue.conflict');
        // Order matters: a later change can depend on an earlier one, so the rest waits behind a "retry". A refusal or a
        // conflict is final for that entry, so the next one is tried at once.
        if (outcome === 'retry') break;
      }
    } finally {
      this.running = false;
    }
  }

  /** Throws away this login's queued writes — for a user who would rather lose them than wait. */
  async discard(): Promise<void> {
    for (const entry of this.pending()) await run(WRITES, 'readwrite', (store) => store.delete(entry.id));
    await this.refresh();
  }

  /** Clears the "it was refused" notice once the user has read it. */
  acknowledge(): void {
    this.refused.set(null);
  }

  private async send(entry: QueuedWrite): Promise<Outcome> {
    try {
      await firstValueFrom(this.http.requestCoded(entry.method, entry.url, entry.body, entry.idempotencyKey));
      return 'sent';
    } catch (error) {
      const status = error instanceof ApiError ? error.status : 0;
      // 409 can mean "the first try is still running": wait a few times, then it is a conflict the user has to hear about.
      if (status === 409) {
        entry.conflicts = (entry.conflicts ?? 0) + 1;
        if (entry.conflicts >= MAX_CONFLICTS) return 'conflict';
        await run(WRITES, 'readwrite', (store) => store.put(entry)).catch(() => undefined);
        return 'retry';
      }
      // The session ended, or the server is down or unreachable: keep it and try again later.
      if (status === 0 || status === 401 || status >= 500) return 'retry';
      // 400/403/404/410/422: the API has made up its mind about this change; keeping it would block the queue forever.
      return 'refused';
    }
  }

  private async drop(entry: QueuedWrite, reasonKey: string | null): Promise<void> {
    await run(WRITES, 'readwrite', (store) => store.delete(entry.id));
    this.items.update((items) => items.filter((item) => item.id !== entry.id));
    if (reasonKey) this.refused.set({ labelKey: entry.labelKey, reasonKey });
  }

  private async refresh(): Promise<void> {
    const all = await run<QueuedWrite[]>(WRITES, 'readonly', (store) => store.getAll()).catch(() => [] as QueuedWrite[]);
    this.lastStamp = all.reduce((latest, item) => Math.max(latest, item.createdAt), this.lastStamp);
    this.items.set(all.sort((a, b) => a.createdAt - b.createdAt));
  }

  private mine(entry: QueuedWrite): boolean {
    return entry.userId === (sessionStorage.getItem('id') ?? '') && entry.companyId === (sessionStorage.getItem('companyId') ?? '');
  }
}
